/**
 * Morse — der Reiter „Geben“. (06.10.2026, Morse-Überarbeitung)
 *
 * Vorher: eine Vorlage aus zehn Zeichen, eine Taste, „Auswerten“ von Hand — gewertet wurde nur, wer daran dachte,
 * und danach war die Taste gesperrt, bis „Nächste Vorlage“ kam (gemessen: nach „K“ und 4,5 s Ruhe nichts
 * ausgewertet, nichts gebucht).
 *
 * Jetzt:
 *   - „Start“ zeigt EIN Ziel groß (Zeichen, Gruppe oder Wort) mit seinem Muster. Martin gibt es mit der großen
 *     Taste (Finger, Maus, Leertaste); der Mithör-Ton klingt, der Dekoder (shared/morse.ts) liest mit.
 *   - Nach einer Pause wertet die Seite von selbst aus (`gebenEntscheid`): gelesen gegen Ziel, Muster gegen
 *     Muster. Richtig → nach einem Moment das nächste Ziel. Falsch → „Nochmal“ (oder einfach wieder drücken)
 *     oder „Weiter“.
 *   - „Stopp“ beendet, fasst zusammen und bucht die Versuche. Vor „Start“ ist die Taste zum Ausprobieren da
 *     (gelesen wird, gewertet nicht).
 *
 * Die Taste am iPhone (Prüfbefunde K1–K3 bleiben): `touch-action: none`, kein Markieren, keine Lupe, kein
 * Doppeltipp-Zoom (touchstart ohne Vorgabe), ein zweiter Finger löst nichts, ein abgebrochener Zeiger
 * (`pointercancel`) zählt nicht, Reiterwechsel und Abgang verwerfen einen gehaltenen Druck.
 *
 * ⚠️ Während der Runde bleibt der Kontext wach (`wachHalten`): sonst schläft er in der App 1,5 s nach dem letzten
 *    Ton, und der erste Punkt nach einer Denkpause bliebe stumm (gemessen mit 150 ms Weckzeit: 0 Mithör-Töne).
 * ⚠️ Der Dekoder beginnt mit der Punktlänge des WIRKSAMEN Tempos (bei 20/10 WpM 120 ms statt 60): ein Anfänger
 *    drückt einen Punkt 120–200 ms lang — mit 60 ms als Schätzung las die Seite sein erstes „E“ als „T“, bis ein
 *    Strich daneben stand. Der Dekoder lernt die Hand danach wie bisher; zwischen den Zielen bleibt sie gelernt.
 */
import { kochAuswertung, kochNeueZeichen, type Umfang } from "../../shared/morse-koch.js";
import { farnsworthDehnung, MORSE_TABELLE, MorseDekoder, musterSilben, UNBEKANNT, type DekoderStand } from "../../shared/morse.js";
import { hapticTap } from "./audio-feedback.js";
import { musterBild, musterZeigen } from "./morse-bild.js";
import { baueSpick, liesHilfe, liesKurztabelle, merkeHilfe, merkeKennengelernt, merkeKurztabelle, zeichneSpick, type GebHilfe } from "./morse-hilfe.js";
import { MorseKennenlernen } from "./morse-kennenlernen.js";
import { MorseUmfang } from "./morse-umfang.js";
import { ANLEITUNG, HILFE, KURZ } from "./morse-texte.js";
import { dekoderStartMs, gebenEntscheid, gebenZiele, GEBEN_RUHE_MS, GEBEN_VERSUCHE_HOECHSTENS, GEBEN_WEITER_MS, rundeErgebnis, type GebenArt } from "./morse-runde.js";
import { Ablauf } from "./morse-ablauf.js";
import { rundenVerlauf } from "./morse-balken.js";
import {
  baueBuehne,
  insBild,
  dauerText,
  hoereMitMeldung,
  el,
  fokusAufStart,
  keinTonText,
  knopf,
  musterSprechen,
  prozent,
  wahl,
  wpmAus,
  type Buehne,
  type BuehnenZustand,
  type MorseUebung,
  type MorseUmgebung,
} from "./morse-teile.js";

/** Die gelernte Hand (Punktlänge in ms) — eine Bequemlichkeit des Geräts, damit die nächste Runde sie gleich kennt. */
const HAND_SCHLUESSEL = "vespera.morse.hand";
/** So lange nach dem Loslassen liest der Takt beim freien Ausprobieren die Stille mit. */
const FREI_TAKT_HOECHSTENS_MS = 4000;

interface Versuch {
  soll: string;
  ist: string;
  richtig: boolean;
  ziel: number;
}

/** Was für ein Muster steht, das es nicht gibt (der Dekoder schreibt `UNBEKANNT`, „*“). */
const NICHT_ERKANNT = "nicht erkannt";

/**
 * Ein Text Zeichen für Zeichen mit seinem Muster als Bild — für das Urteil beim Geben. Ein nicht erkanntes Zeichen
 * („*“) bekommt KEIN Muster (Prüfbefund K6: es wurde zu „?“ und mit ··−−·· gezeichnet), nur die Worte „nicht erkannt“.
 */
export function urteilZeichen(text: string): Node[] {
  const aus: Node[] = [];
  for (const z of text.replace(/\s+/g, "")) {
    if (z === UNBEKANNT) {
      aus.push(document.createTextNode(" "), el("span", "morse-urteil-unbekannt", NICHT_ERKANNT));
      continue;
    }
    const teil = el("span", "morse-urteil-teil");
    const bild = musterBild(MORSE_TABELLE[z] ?? "", "s");
    bild.setAttribute("aria-hidden", "true");
    teil.append(el("span", "morse-urteil-zeichen", z), bild);
    aus.push(document.createTextNode(" "), teil);
  }
  return aus;
}

export class MorseGeben implements MorseUebung {
  private readonly b: Buehne;
  private readonly bereitTeil: HTMLElement;
  private readonly artWahl: ReturnType<typeof wahl<GebenArt>>;
  private readonly zielTeil: HTMLElement;
  private readonly zielZeichen: HTMLElement;
  private readonly zielMuster: HTMLElement;
  /** Mit Hilfe: die Silben der Vorlage („dah-di-dah“). */
  private readonly zielSilben: HTMLElement;
  private readonly anhoeren: HTMLButtonElement;
  private readonly kennen: MorseKennenlernen;
  /** „Üben mit“: Meine Zeichen | Alle Zeichen (08.10.2026) — die Wahl, und was die laufende Runde beim Start davon nahm. */
  private readonly umfangWahl: MorseUmfang;
  private umfang: Umfang = "meine";
  private readonly hilfeWahl: ReturnType<typeof wahl<GebHilfe>>;
  private readonly hilfeText: HTMLElement;
  /**
   * Die Zeichen der Stufe mit Muster, klein, UNTER der Taste: mit Hilfe „An“ von selbst, sonst nach dem Schalter
   * „Tabelle“ (`kurzKnopf`, gemerkt, Standard aus). Eine Liste für beides — sie steht nie doppelt da.
   */
  private readonly spick: HTMLElement;
  private readonly kurz: HTMLElement;
  private readonly kurzKnopf: HTMLButtonElement;
  private readonly kurzZustand: HTMLElement;
  private kurzAn = false;
  private hilfe: GebHilfe = "aus";
  /** Die Karte „Gelesen“ — nach der Auswertung trägt sie auch das Urteil (die Taste darunter rückt nie). */
  private readonly lese: HTMLElement;
  private readonly leseTitel: HTMLElement;
  private readonly gelesen: HTMLElement;
  private readonly offen: HTMLElement;
  private readonly urteilMuster: HTMLElement;
  /** Solange nichts gelesen ist: ein Satz, was hier gleich erscheint (die Karte steht sonst leer da). */
  private readonly leseLeer: HTMLElement;
  private readonly urteilAnsage: HTMLElement;
  private readonly urteilKnoepfe: HTMLElement;
  private readonly fertigTeil: HTMLElement;
  private readonly taste: HTMLButtonElement;
  private readonly tasteHinweis: HTMLElement;

  private art: GebenArt = "zeichen";
  private zustand: BuehnenZustand = "bereit";
  private ziele: string[] = [];
  private index = 0;
  /** Ist das laufende Ziel gerade ausgewertet (dann löst der nächste Druck „Nochmal“ bzw. „Weiter“ aus)? */
  private ausgewertet: { richtig: boolean } | null = null;
  private versuche: Versuch[] = [];
  /** Token, Uhr, wacher Ton, aktive Zeit, Buchung (morse-ablauf.ts). */
  private readonly ablauf: Ablauf;

  // Die Taste
  private dekoder: MorseDekoder | null = null;
  private gedruecktSeit: number | null = null;
  private losgelassenAm: number | null = null;
  /** Wann zuletzt losgelassen wurde, BEVOR der laufende Druck begann (für einen verworfenen Druck). */
  private losgelassenVorher: number | null = null;
  private losTon: (() => void) | null = null;
  private takt: ReturnType<typeof setInterval> | null = null;
  /** Wer die Taste hält: ein Zeiger (seine Kennung) oder die Tastatur — ein zweiter Finger löst nichts (K1). */
  private halter: number | "tastatur" | null = null;
  /** Was „Gelesen“ zuletzt zeigte — die Live-Region wird nur bei einer Änderung neu geschrieben (K5). */
  private gelesenZuletzt: string | null = null;
  private versuchSeit = 0;
  /** Nach `GEBEN_RUHE_MS` ohne Druck pausiert die Runde von selbst (W1). */
  private ruheUhr: ReturnType<typeof setTimeout> | null = null;
  /** Versuche bis hierher stecken schon in einer Buchung (W4). */
  private gebuchtBis = 0;
  private rundeNr = 0;
  /** Womit der Dekoder diese Runde begann — und die gelernte Hand der letzten Runde (K1). */
  private startMs = 0;
  private hand: number | null = null;


  constructor(
    private readonly u: MorseUmgebung,
    tafel: HTMLElement,
  ) {
    this.b = baueBuehne(
      "Übung Geben",
      (rolle) => (rolle === "start" ? this.start() : this.stopp("knopf")),
      (rolle) => (rolle === "pause" ? this.pause("knopf") : this.weiter()),
      u.zeigeTabelle ? () => u.zeigeTabelle?.() : undefined,
    );
    tafel.appendChild(this.b.wurzel);
    this.ablauf = new Ablauf(u, "geben", this.b);
    try {
      const h = Number(localStorage.getItem(HAND_SCHLUESSEL));
      if (h) this.hand = h;
    } catch {
      /* Privatmodus: dann eben ohne */
    }

    this.fertigTeil = el("div", "morse-fertig");

    // Das Ziel: groß, mit Muster, zum Anhören
    this.zielTeil = el("div", "morse-ziel");
    const zielKopf = el("p", "morse-feld-titel", "Gib");
    this.zielZeichen = el("p", "morse-ziel-zeichen");
    this.zielMuster = el("p", "morse-ziel-muster");
    this.zielMuster.setAttribute("role", "img");
    this.zielSilben = el("p", "morse-ziel-silben");
    this.anhoeren = knopf("Anhören", "morse-knopf morse-knopf--klein", () => this.hoereZiel());
    this.zielTeil.append(zielKopf, this.zielZeichen, this.zielMuster, this.zielSilben, this.anhoeren);

    // Was die Seite liest — und danach in DERSELBEN Karte das Urteil: Ziel gegen Gegebenes, Muster gegen Muster.
    this.lese = el("div", "morse-gelesen-karte");
    const leseKopf = el("p", "morse-gelesen-kopf");
    const form = el("span", "morse-urteil-form");
    form.setAttribute("aria-hidden", "true");
    this.leseTitel = el("span", "morse-gelesen-titel", "Gelesen");
    leseKopf.append(form, this.leseTitel);
    leseKopf.setAttribute("aria-hidden", "true");
    this.gelesen = el("p", "morse-gelesen");
    this.gelesen.setAttribute("aria-live", "polite");
    this.gelesen.setAttribute("aria-label", "Gelesen");
    this.offen = el("p", "morse-offen");
    this.offen.setAttribute("aria-hidden", "true");
    this.urteilMuster = el("p", "morse-urteil-muster");
    this.urteilMuster.setAttribute("aria-hidden", "true");
    this.leseLeer = el("p", "morse-gelesen-leer", "Halte die Taste: kurz für ·, lang für −. Hier steht, was die Seite liest.");
    this.leseLeer.setAttribute("aria-hidden", "true");
    this.urteilAnsage = el("span", "morse-nur-vorlesen");
    this.urteilAnsage.setAttribute("aria-live", "polite");
    this.urteilKnoepfe = el("div", "morse-urteil-knoepfe");
    this.urteilKnoepfe.append(
      knopf("Nochmal", "morse-knopf morse-knopf--klein", () => this.nochmal()),
      knopf("Weiter", "morse-knopf morse-knopf--klein", () => this.naechstesZiel()),
    );
    this.urteilKnoepfe.hidden = true;
    this.lese.append(leseKopf, this.urteilKnoepfe, this.gelesen, this.offen, this.urteilMuster, this.leseLeer, this.urteilAnsage);

    // Bereit: Kennenlernen (wenn ein Zeichen neu ist), wie es geht, was gegeben wird, Hilfe
    this.bereitTeil = el("div", "morse-bereit");
    this.kennen = new MorseKennenlernen(u, (t) => this.b.melde(t, "fehler"), "reiter=geben");
    this.umfangWahl = new MorseUmfang(u, () => this.zeichneWahl());
    this.bereitTeil.append(this.kennen.wurzel, el("p", "morse-anleitung", ANLEITUNG.geben), this.umfangWahl.wurzel);
    this.artWahl = wahl<GebenArt>(
      "Was du gibst",
      [
        { id: "zeichen", text: "Zeichen" },
        { id: "gruppen", text: "Gruppen" },
        { id: "woerter", text: "Wörter" },
      ],
      (id) => {
        this.art = id;
        this.zeichneWahl();
      },
    );
    const hilfeBlock = el("div", "morse-hilfe-wahl");
    this.hilfeWahl = wahl<GebHilfe>(
      HILFE.titel,
      (["aus", "an"] as const).map((id) => ({ id, text: HILFE.gebenWahl[id] })),
      (id) => {
        merkeHilfe("geben", id);
        this.zeichneWahl();
      },
    );
    this.hilfeText = el("p", "morse-hinweis");
    hilfeBlock.append(el("p", "morse-feld-titel", HILFE.titel), this.hilfeWahl.wurzel, this.hilfeText);
    this.bereitTeil.append(this.artWahl.wurzel, hilfeBlock, el("p", "morse-tastenhinweis", "Leertaste:\u00a0Start, danach\u00a0morst\u00a0sie · Esc:\u00a0Stopp"));
    this.spick = baueSpick(u.stand().stufe, { kurz: true });

    // Die Taste — immer an derselben Stelle, unter allem
    this.taste = el("button", "morse-taste");
    this.taste.type = "button";
    this.taste.setAttribute("aria-label", "Morsetaste: gedrückt halten. Mit Leertaste oder Eingabetaste bedienbar.");
    this.taste.setAttribute("aria-pressed", "false");
    this.tasteHinweis = el("span", "morse-taste-hinweis", "zum Ausprobieren");
    this.taste.append(el("span", "morse-taste-name", "Taste"), this.tasteHinweis);
    this.verdrahteTaste();

    // Die Kurztabelle UNTER der Taste: der Schalter „Tabelle“ steht fest, die Liste kommt darunter — so rückt die Taste
    // nie (nichts über ihr ändert sich), und die Zeichen der Stufe bleiben im Blick. Standard aus, gemerkt (morse-hilfe.ts).
    this.kurzAn = liesKurztabelle();
    this.kurz = el("div", "morse-kurz");
    this.kurzKnopf = el("button", "morse-kurz-knopf");
    this.kurzKnopf.type = "button";
    this.kurzKnopf.setAttribute("aria-label", `${KURZ.knopf}: ${KURZ.beschreibung}`);
    this.kurzZustand = el("span", "morse-kurz-zustand");
    this.kurzZustand.setAttribute("aria-hidden", "true");
    this.kurzKnopf.append(el("span", "morse-kurz-name", KURZ.knopf), this.kurzZustand);
    this.kurzKnopf.addEventListener("click", (ev) => this.schalteKurz(ev));
    this.kurz.append(this.kurzKnopf, this.spick);
    this.b.flaeche.append(this.fertigTeil, this.zielTeil, this.bereitTeil, this.lese, this.taste, this.kurz);
    this.zeige("bereit", "");
    this.zeichneWahl();
    this.zeigeGelesen({ text: "", offen: "" });
  }

  get laeuft(): boolean {
    return this.zustand === "laeuft";
  }
  get pausiert(): boolean {
    return this.zustand === "pause";
  }

  /* ── Bedienung ──────────────────────────────────────────────────── */

  start(): void {
    if (this.zustand === "laeuft" || this.zustand === "pause") return;
    const s = this.u.stand();
    const guete: Record<string, number> = {};
    for (const z of s.zeichen) guete[z.zeichen] = z.guete;
    // Womit geübt wird, legt der Start fest (die Wahl ist in der Runde gesperrt).
    this.umfang = this.umfangWahl.umfang();
    const g = gebenZiele(s.stufe, this.art, { guete, zufall: () => this.u.zufall(), umfang: this.umfang });
    this.ziele = g.ziele;
    if (this.umfang === "meine") merkeKennengelernt(kochNeueZeichen(s.stufe));
    this.kennen.zeichne();
    zeichneSpick(this.spick, s.stufe, { umfang: this.umfang, zeichen: s.zeichen });
    this.ablauf.neueRunde(this.umfang);
    this.rundeNr++;
    this.versuche = [];
    this.gebuchtBis = 0;
    this.tasteVerwerfen();
    this.taktStopp();
    this.startMs = dekoderStartMs(s.einstellungen, this.hand);
    this.dekoder = this.neuerDekoder();
    this.b.melde(g.art !== this.art ? "Auf dieser Stufe gibt es kein Wort mit dem neuen Zeichen — darum Gruppen." : "", "info");
    this.zeige("laeuft");
    // In der Geste wecken und wach halten — der erste Druck soll sofort klingen.
    this.halteWach(`start reiter=geben art=${g.art} stufe=${s.stufe} ziele=${this.ziele.length} hilfe=${this.hilfe}${this.umfang === "alle" ? " umfang=alle" : ""}`);
    this.ablauf.zeitLaeuft();
    this.zeigeZiel(0);
    // Die Leertaste gehört ab jetzt der Taste.
    this.taste.focus({ preventScroll: true });
    insBild(this.b.wurzel);
    this.weckeRuhe();
  }

  stopp(grund: string): void {
    if (this.zustand !== "laeuft" && this.zustand !== "pause") return;
    // Ist gerade etwas gegeben und noch nicht ausgewertet, zählt es noch.
    if (this.zustand === "laeuft" && !this.ausgewertet && this.dekoder && this.gedruecktSeit === null) {
      const s = this.dekoder.abschliessen();
      if (s.text) this.merkeVersuch(s.text);
    }
    this.beende(grund);
  }

  pause(grund: string, gehtWeg = false): void {
    if (this.zustand !== "laeuft") return;
    this.ablauf.allesAus();
    this.tasteVerwerfen();
    this.ruheAus();
    this.ablauf.zeitHalt();
    this.ablauf.lassWach();
    // Was gegeben ist, sofort buchen — geht die Seite ganz, mit keepalive (W4).
    void this.bucheRest(gehtWeg);
    this.dekoder?.leeren();
    this.losgelassenAm = null;
    this.ausgewertet = null;
    this.urteilLeeren();
    this.zeigeGelesen(this.dekoder?.stand() ?? { text: "", offen: "" });
    this.u.spur(`pause reiter=geben grund=${grund} ziel=${this.index + 1}`);
    this.zeige("pause");
  }

  weiter(): void {
    if (this.zustand !== "pause") return;
    this.b.melde("");
    this.zeige("laeuft");
    this.halteWach("weiter reiter=geben");
    this.ablauf.zeitLaeuft();
    this.zeigeZiel(this.index);
    this.taste.focus({ preventScroll: true });
    this.weckeRuhe();
  }

  leertaste(art: "keydown" | "keyup", wiederholt: boolean): boolean {
    if (this.zustand === "laeuft") {
      // Läuft die Runde, morst die Leertaste — wo auch immer der Fokus gerade liegt.
      if (art === "keydown" && !wiederholt && this.halter === null) this.tasteRunter("tastatur");
      if (art === "keyup" && this.halter === "tastatur") this.tasteHoch();
      return true;
    }
    if (art === "keyup") return this.zustand !== "bereit";
    if (wiederholt) return true;
    if (this.zustand === "pause") this.weiter();
    else this.start();
    return true;
  }

  ruhe(grund: string, gehtWeg = false): void {
    this.tasteVerwerfen();
    if (this.zustand === "laeuft") this.pause(grund, gehtWeg);
    else this.taktStopp();
  }

  /** Das Fenster verliert den Fokus (K2): eine gehaltene Taste los — Ton und Miete enden, die Runde läuft weiter. */
  loeseTaste(): void {
    this.tasteVerwerfen();
  }

  standGeaendert(): void {
    if (this.zustand !== "laeuft" && this.zustand !== "pause") {
      this.zeichneWahl();
      if (!this.gedruecktSeit) this.dekoder = null;
    }
  }

  zerstoere(): void {
    this.ablauf.allesAus();
    this.tasteVerwerfen();
    this.ruheAus();
    this.taktStopp();
    this.ablauf.lassWach();
  }

  /* ── Ablauf ─────────────────────────────────────────────────────── */

  private zeigeZiel(i: number): void {
    this.index = i;
    this.ausgewertet = null;
    this.urteilLeeren();
    this.dekoder?.leeren();
    this.losgelassenAm = null;
    this.versuchSeit = 0;
    this.zeigeGelesen(this.dekoder?.stand() ?? { text: "", offen: "" });
    const ziel = this.ziele[i] ?? "";
    this.zielZeichen.textContent = ziel;
    this.zielZeichen.setAttribute("aria-label", `Gib: ${[...ziel].join(" ")}`);
    // Das Muster als Bild (Kreis und Balken, morse-bild.ts) — je Zeichen eins; mit Hilfe dazu die Silben.
    this.zielMuster.textContent = "";
    for (const z of ziel) {
      const b = musterBild(MORSE_TABELLE[z] ?? "", "l");
      b.setAttribute("aria-hidden", "true");
      this.zielMuster.appendChild(b);
    }
    this.zielMuster.setAttribute("aria-label", [...ziel].map((z) => `${z}: ${musterSprechen(MORSE_TABELLE[z] ?? "")}`).join(", "));
    this.zielSilben.textContent = [...ziel].map((z) => musterSilben(MORSE_TABELLE[z] ?? "")).join(" · ");
    this.b.zaehler(`${this.art === "zeichen" ? "Zeichen" : this.art === "woerter" ? "Wort" : "Gruppe"} ${i + 1} von ${this.ziele.length}`);
    this.zeichneVerlauf();
    this.tasteHinweis.textContent = "drücken und halten";
  }

  /** Den laufenden Versuch auswerten (der Takt entscheidet, `gebenEntscheid`). */
  private werteVersuch(): void {
    if (!this.dekoder || this.zustand !== "laeuft" || this.ausgewertet) return;
    this.taktStopp();
    const s = this.dekoder.abschliessen();
    this.zeigeGelesen(s);
    if (!s.text) return;
    const v = this.merkeVersuch(s.text);
    this.ausgewertet = { richtig: v.richtig };
    this.zeichneUrteil(v);
    this.dekoder.leeren();
    this.losgelassenAm = null;
    if (this.versuche.length >= GEBEN_VERSUCHE_HOECHSTENS) {
      this.ablauf.spaeter(GEBEN_WEITER_MS, () => this.beende("versuche"));
      return;
    }
    if (v.richtig) {
      this.tasteHinweis.textContent = "weiter mit einem Druck";
      this.ablauf.spaeter(GEBEN_WEITER_MS, () => this.naechstesZiel());
    } else {
      this.tasteHinweis.textContent = "nochmal: einfach drücken";
      this.urteilKnoepfe.hidden = false;
    }
  }

  private merkeVersuch(ist: string): Versuch {
    const soll = this.ziele[this.index] ?? "";
    const a = kochAuswertung(soll, ist);
    const v: Versuch = { soll, ist, richtig: a.richtig === a.gesamt && a.zuviel === 0, ziel: this.index };
    if (this.versuche.length < GEBEN_VERSUCHE_HOECHSTENS) this.versuche.push(v);
    this.zeichneVerlauf();
    return v;
  }

  private nochmal(): void {
    if (this.zustand !== "laeuft") return;
    this.ablauf.uhrAus();
    this.zeigeZiel(this.index);
    this.taste.focus({ preventScroll: true });
  }

  private naechstesZiel(): void {
    if (this.zustand !== "laeuft") return;
    this.ablauf.uhrAus();
    if (this.index + 1 >= this.ziele.length) {
      this.beende("fertig");
      return;
    }
    this.zeigeZiel(this.index + 1);
    this.taste.focus({ preventScroll: true });
  }

  private beende(grund: string): void {
    this.ablauf.allesAus();
    this.tasteVerwerfen();
    this.ruheAus();
    this.taktStopp();
    this.ablauf.zeitHalt();
    this.ablauf.lassWach();
    // Die Hand gilt nur als gemessen, wenn der Dekoder seine Schätzung geändert hat (Punkt UND Strich gesehen) —
    // sonst stünde hier bloß das Starttempo. Gelernt, gilt sie als Start der nächsten Runde (K1).
    const geschaetzt = this.dekoder?.stand().punktMs ?? 0;
    const hand = geschaetzt && Math.abs(geschaetzt - this.startMs) > 0.5 ? geschaetzt : 0;
    if (hand) this.merkeHand(hand);
    this.u.spur(`stopp reiter=geben grund=${grund} versuche=${this.versuche.length} ziele=${this.index + 1}/${this.ziele.length} ton=${this.u.spieler.zustand()}`);
    this.zeige("fertig", `${new Set(this.versuche.map((v) => v.ziel)).size} von ${this.ziele.length} gegeben`);
    this.urteilLeeren();
    this.dekoder = null;
    this.zeigeGelesen({ text: "", offen: "" });
    this.zeichneFertig(hand);
    fokusAufStart(this.b);
    if (!this.versuche.length) {
      this.b.melde("Nichts gegeben — nichts gespeichert.", "info");
      return;
    }
    const nr = this.rundeNr;
    this.b.melde("Speichere …", "info");
    void this.bucheRest(false).then(() => {
      if (nr === this.rundeNr && this.zustand === "fertig") this.b.melde(this.ablauf.hatOffenes ? "" : "Gespeichert.", "info");
    });
  }

  /** Was gegeben und noch nicht gebucht ist, buchen (Pause, Stopp, Ende, Seite verdeckt). Wartet, bis alles da ist. */
  private async bucheRest(gehtWeg: boolean): Promise<void> {
    const neu = this.versuche.slice(this.gebuchtBis);
    if (neu.length) {
      this.gebuchtBis = this.versuche.length;
      void this.ablauf.sende(this.ablauf.satz(neu.map((v) => ({ soll: v.soll, ist: v.ist }))), gehtWeg);
    }
    await this.ablauf.warte();
  }

  private merkeHand(ms: number): void {
    this.hand = ms;
    try {
      localStorage.setItem(HAND_SCHLUESSEL, String(Math.round(ms)));
    } catch {
      /* Privatmodus */
    }
  }

  /**
   * In der Runde ohne Druck: nach `GEBEN_RUHE_MS` pausiert sie von selbst (Prüfbefund W1). Sonst hielte sie den
   * Ton unbegrenzt wach — in der App bliebe das Wach-Wort taub, solange die Seite offen ist.
   */
  private weckeRuhe(): void {
    this.ruheAus();
    if (this.zustand !== "laeuft") return;
    this.ruheUhr = setTimeout(() => {
      this.ruheUhr = null;
      if (this.zustand !== "laeuft" || this.gedruecktSeit !== null) return;
      this.pause("ruhe");
      this.b.melde(`Pausiert — ${Math.round(GEBEN_RUHE_MS / 1000)} Sekunden kam kein Druck. Drück „Weiter“, wenn du weitermachen willst.`, "info");
    }, GEBEN_RUHE_MS);
  }

  private ruheAus(): void {
    if (this.ruheUhr) clearTimeout(this.ruheUhr);
    this.ruheUhr = null;
  }

  /* ── Anzeige ────────────────────────────────────────────────────── */

  /** Der Verlauf (morse-balken.ts): ein Strich je Ziel — zählt der erste Versuch (wie „auf Anhieb“ in der Auswertung). */
  private zeichneVerlauf(): void {
    if (this.zustand === "bereit" || this.zustand === "fehler" || !this.ziele.length) return this.b.verlauf(null);
    const ergebnisse = this.ziele.map((_, i) => this.versuche.find((v) => v.ziel === i)?.richtig);
    this.b.verlauf(rundenVerlauf(ergebnisse, { gesamt: this.ziele.length, jetzt: this.zustand === "fertig" ? null : this.index }));
  }

  private zeige(z: BuehnenZustand, zaehler?: string): void {
    this.zustand = z;
    this.b.setze(z, { zaehler });
    this.zeichneVerlauf();
    const uebung = z === "laeuft" || z === "pause";
    // Hilfe: in der Runde gilt, was beim Start galt; davor die gemerkte Wahl.
    if (!uebung) this.hilfe = liesHilfe(this.u.stand().stufe).geben;
    this.zielSilben.hidden = this.hilfe !== "an";
    this.zeigeKurz();
    this.zielTeil.hidden = !uebung;
    this.bereitTeil.hidden = uebung;
    this.fertigTeil.hidden = z !== "fertig";
    this.taste.dataset.zustand = z;
    // Die Karte „Gelesen“ steht in JEDEM Zustand (leer mit ihrem Satz) — nie erst beim ersten Druck: sie liegt ÜBER der
    // Taste. 08.10.2026 gemessen: erschien sie erst beim Ausprobieren, rückte die Taste 140 Punkte nach unten und jeder
    // weitere Druck an derselben Stelle traf die Karte („T----“, Martin: „geht überhaupt nicht mehr“).
    if (!uebung) this.tasteHinweis.textContent = "zum Ausprobieren";
    if (z === "pause") this.tasteHinweis.textContent = "Pause — „Weiter“ drücken";
  }

  private zeichneWahl(): void {
    this.artWahl.setze(this.art);
    const s = this.u.stand();
    this.kennen.zeichne();
    if (this.zustand !== "laeuft" && this.zustand !== "pause") this.hilfe = liesHilfe(s.stufe).geben;
    this.hilfeWahl.setze(this.hilfe);
    this.hilfeText.textContent = HILFE.geben[this.hilfe];
    this.zielSilben.hidden = this.hilfe !== "an";
    this.zeigeKurz();
    // Die Liste unter der Taste: in der Runde so, wie sie beim Start war; sonst nach der Wahl „Üben mit“.
    const laeuft = this.zustand === "laeuft" || this.zustand === "pause";
    const umfang = this.umfangWahl.zeichne();
    zeichneSpick(this.spick, s.stufe, { umfang: laeuft ? this.umfang : umfang, zeichen: s.zeichen });
  }

  /**
   * Die Kurztabelle zeigen oder verbergen. Sichtbar ist sie, wenn der Schalter an ist ODER die Hilfe „An“ gilt (die
   * zeigt dieselben Zeichen); dann steht der Schalter fest auf „ein“ (`aria-disabled`, kein Tipp ändert etwas) und
   * sagt in Worten warum. Der Schalter selbst steht immer da — es rückt nur, was UNTER ihm liegt.
   */
  private zeigeKurz(): void {
    const durchHilfe = this.hilfe === "an";
    const sichtbar = this.kurzAn || durchHilfe;
    this.spick.hidden = !sichtbar;
    this.kurzKnopf.setAttribute("aria-pressed", String(sichtbar));
    this.kurzKnopf.setAttribute("aria-disabled", String(durchHilfe));
    this.kurzKnopf.dataset.an = String(sichtbar);
    this.kurzZustand.textContent = durchHilfe ? KURZ.durchHilfe : sichtbar ? KURZ.an : KURZ.aus;
  }

  private schalteKurz(ev: MouseEvent): void {
    if (this.hilfe === "an") return;
    this.kurzAn = !this.kurzAn;
    merkeKurztabelle(this.kurzAn);
    this.zeigeKurz();
    // Ein Tipp nimmt der Taste den Fokus — läuft die Runde, gehört die Leertaste ihr wieder (nicht bei Tastatur-Klick).
    if (ev.detail > 0 && this.zustand === "laeuft") this.taste.focus({ preventScroll: true });
  }

  /** Das Urteil in die Karte „Gelesen“: Form UND Wort, Ziel gegen Gegebenes mit Mustern. */
  private zeichneUrteil(v: Versuch): void {
    this.lese.dataset.richtig = String(v.richtig);
    this.leseTitel.textContent = v.richtig ? "Richtig" : "Nicht ganz";
    // Muster gegen Muster, als Bild: so sieht Martin, WAS die Hand anders gemacht hat.
    const ist = v.ist.replace(/\*/g, ` ${NICHT_ERKANNT} `).replace(/\s+/g, " ").trim();
    const mitMuster = urteilZeichen;
    this.offen.textContent = "";
    this.urteilMuster.textContent = "";
    if (v.richtig) this.urteilMuster.append(...mitMuster(v.soll));
    else
      this.urteilMuster.append(
        el("span", "morse-urteil-wer", "Ziel"),
        ...mitMuster(v.soll),
        document.createTextNode(" "),
        el("span", "morse-urteil-trenner", "·"),
        document.createTextNode(" "),
        el("span", "morse-urteil-wer", "du"),
        ...(v.ist ? mitMuster(v.ist) : [document.createTextNode(" –")]),
      );
    this.urteilAnsage.textContent = v.richtig ? `Richtig: ${v.soll}.` : `Nicht ganz. Gelesen ${ist || "nichts"}, Ziel ${v.soll}.`;
    this.leseLeer.hidden = true;
  }

  private urteilLeeren(): void {
    delete this.lese.dataset.richtig;
    this.leseTitel.textContent = "Gelesen";
    this.urteilMuster.textContent = "";
    this.urteilAnsage.textContent = "";
    this.urteilKnoepfe.hidden = true;
  }

  private zeichneFertig(hand: number): void {
    const t = this.fertigTeil;
    t.textContent = "";
    if (!this.versuche.length) {
      t.appendChild(el("p", "morse-ergebnis-text", "Diese Runde ist ohne Versuch zu Ende."));
      return;
    }
    const e = rundeErgebnis(this.versuche);
    const ziele = new Set(this.versuche.map((v) => v.ziel));
    const anhieb = [...ziele].filter((z) => this.versuche.find((v) => v.ziel === z)?.richtig).length;
    const kopf = el("div", "morse-ergebnis");
    kopf.append(
      el("p", "morse-ergebnis-zahl", prozent(e.quote)),
      el(
        "p",
        "morse-ergebnis-text",
        `${anhieb} von ${ziele.size} auf Anhieb richtig · ${this.versuche.length} ${this.versuche.length === 1 ? "Versuch" : "Versuche"} · ${dauerText(this.ablauf.aktivMs)}${
          hand ? ` · deine Hand etwa ${wpmAus(hand)} WpM` : ""
        }`,
      ),
    );
    t.appendChild(kopf);
  }

  private zeigeGelesen(s: Pick<DekoderStand, "text" | "offen">): void {
    // K5: die Live-Region nur bei einer Änderung schreiben — der Takt fragt alle 60 ms.
    const text = s.text.replace(/\*/g, "?");
    if (text !== this.gelesenZuletzt) {
      this.gelesen.textContent = text;
      this.gelesenZuletzt = text;
    }
    const muster = musterZeigen(s.offen);
    if (this.offen.textContent !== muster) this.offen.textContent = muster;
    this.leseLeer.hidden = !!(text || muster || this.lese.dataset.richtig);
  }

  /** Das Ziel anhören — kam kein Ton, steht es in der Statuszeile. */
  private hoereZiel(): void {
    const ziel = this.ziele[this.index];
    if (!ziel || this.gedruecktSeit !== null) return;
    this.weckeRuhe();
    void hoereMitMeldung(this.u, ziel, (t) => this.b.melde(t, "fehler"), "reiter=geben abspielen");
  }

  /* ── Die Taste ──────────────────────────────────────────────────── */

  private verdrahteTaste(): void {
    const t = this.taste;
    t.addEventListener("pointerdown", (ev) => {
      if (ev.button !== 0) return;
      ev.preventDefault();
      if (this.halter !== null) return; // K1: ein zweiter Finger zählt nicht
      try {
        t.setPointerCapture(ev.pointerId);
      } catch {
        /* ältere WebKit-Fassungen, künstliche Zeiger */
      }
      this.tasteRunter(ev.pointerId);
    });
    for (const art of ["pointerup", "lostpointercapture"] as const) {
      t.addEventListener(art, (ev) => {
        if (ev.pointerId === this.halter) this.tasteHoch();
      });
    }
    // K2: ein abgebrochener Zeiger (das System übernimmt die Geste) ist kein Druck.
    t.addEventListener("pointercancel", (ev) => {
      if (ev.pointerId === this.halter) this.tasteVerwerfen();
    });
    // iPhone: kein Markieren, keine Lupe, kein Doppeltipp-Zoom — die Zeiger-Ereignisse kommen trotzdem.
    t.addEventListener("touchstart", (ev) => ev.preventDefault(), { passive: false });
    t.addEventListener("keydown", (ev) => {
      if (ev.key !== " " && ev.key !== "Enter") return;
      ev.preventDefault();
      ev.stopPropagation();
      if (!ev.repeat && this.halter === null) this.tasteRunter("tastatur");
    });
    // Das Loslassen NIE verschlucken (Prüfbefund K3): wer es braucht (die Sprechtaste der App), soll es bekommen.
    t.addEventListener("keyup", (ev) => {
      if (ev.key !== " " && ev.key !== "Enter") return;
      ev.preventDefault();
      if (this.halter === "tastatur") this.tasteHoch();
    });
    t.addEventListener("blur", () => {
      if (this.halter === "tastatur") this.tasteVerwerfen();
    });
    // Ein Klick (Tastatur, Bildschirmleser) zählt nicht noch einmal — gezählt wird Drücken und Loslassen.
    t.addEventListener("click", (ev) => ev.preventDefault());
    t.addEventListener("contextmenu", (ev) => ev.preventDefault());
  }

  private neuerDekoder(): MorseDekoder {
    const e = this.u.stand().einstellungen;
    return new MorseDekoder(this.startMs || dekoderStartMs(e, this.hand), { dehnung: farnsworthDehnung(e) });
  }

  private tasteRunter(halter: number | "tastatur"): void {
    if (this.gedruecktSeit !== null || this.zustand === "pause") return;
    if (this.zustand === "laeuft" && this.ausgewertet) {
      if (this.ausgewertet.richtig) {
        // Nach „Richtig“ heißt der Druck NUR „weiter“ (Prüfbefund W2): er gehörte sonst schon zum nächsten, noch
        // ungesehenen Ziel — „Nicht ganz“ und ein falsch gebuchter Versuch. Kein Ton, kein Lesen.
        if (this.index + 1 >= this.ziele.length) this.beende("fertig");
        else {
          this.ablauf.uhrAus();
          this.zeigeZiel(this.index + 1);
          this.weckeRuhe();
        }
        return;
      }
      // Falsch: der Druck ist der neue Versuch am selben, sichtbaren Ziel.
      this.zeigeZiel(this.index);
    }
    this.ruheAus();
    this.dekoder ??= this.neuerDekoder();
    const t = this.u.jetzt();
    if (!this.versuchSeit) this.versuchSeit = t;
    if (this.losgelassenAm !== null) this.dekoder.stille(t - this.losgelassenAm);
    this.halter = halter;
    this.gedruecktSeit = t;
    this.losgelassenVorher = this.losgelassenAm;
    this.losgelassenAm = null;
    this.taktStopp();
    this.taste.setAttribute("aria-pressed", "true");
    this.taste.dataset.gedrueckt = "true";
    hapticTap(10);
    // Wer die Taste drückt, hört nur sie — eine Hörspur aus der Einführung schweigt.
    this.u.hoerspur?.stille("taste");
    this.losTon = this.u.spieler.halte({ ton: this.u.stand().einstellungen.ton });
  }

  private tasteHoch(): void {
    if (this.gedruecktSeit === null || !this.dekoder) return;
    const t = this.u.jetzt();
    const dauer = t - this.gedruecktSeit;
    this.gedruecktSeit = null;
    this.halter = null;
    this.losgelassenAm = t;
    this.losTon?.();
    this.losTon = null;
    this.taste.setAttribute("aria-pressed", "false");
    delete this.taste.dataset.gedrueckt;
    this.zeigeGelesen(this.dekoder.druck(dauer));
    this.weckeRuhe();
    // Ein Takt liest die wachsende Stille: so schließt ein Zeichen, ohne dass der nächste Druck kommen muss —
    // und in der Runde entscheidet er, wann ausgewertet wird.
    this.takt = setInterval(() => {
      if (!this.dekoder || this.losgelassenAm === null) return this.taktStopp();
      const still = this.u.jetzt() - this.losgelassenAm;
      const s = this.dekoder.stille(still);
      this.zeigeGelesen(s);
      if (this.zustand === "laeuft" && !this.ausgewertet) {
        if (gebenEntscheid(this.ziele[this.index] ?? "", s, still) === "fertig") this.werteVersuch();
      } else if (still > FREI_TAKT_HOECHSTENS_MS) {
        this.taktStopp();
      }
    }, 60);
  }

  /** Einen gehaltenen Druck verwerfen, als hätte es ihn nicht gegeben (Abbruch, Reiterwechsel, Abgang). */
  private tasteVerwerfen(): void {
    if (this.gedruecktSeit === null) return;
    this.gedruecktSeit = null;
    this.halter = null;
    this.losTon?.();
    this.losTon = null;
    this.taste.setAttribute("aria-pressed", "false");
    delete this.taste.dataset.gedrueckt;
    // Die Stille vor dem verworfenen Druck läuft weiter.
    this.losgelassenAm = this.losgelassenVorher;
    // Nachblick W1: `tasteRunter` hielt die Ruhe-Uhr an, neu gestellt wurde sie nur beim Loslassen. Ein
    // VERWORFENER Druck (Fenster ohne Fokus, `pointercancel` durch eine iOS-Geste, Taste ohne Fokus) ließ die Runde
    // so endlos laufen — Ton wach, Wach-Wort taub. Läuft die Runde, läuft die Uhr wieder.
    if (this.zustand === "laeuft") this.weckeRuhe();
  }

  private taktStopp(): void {
    if (this.takt) clearInterval(this.takt);
    this.takt = null;
  }

  /** Den Ton für die Runde wach halten — ohne Ton geht das Geben trotzdem, aber Martin soll wissen, warum es still ist. */
  private halteWach(spurZeile: string): void {
    this.ablauf.halteWach(spurZeile, () => {
      if (this.zustand === "laeuft") this.b.melde(`${keinTonText(this.u.spieler.zustand())} Geben geht auch ohne Ton.`, "fehler");
    });
  }
}
